import * as THREE from 'three';
import { GUN_RELOAD_TICKS } from '../../shared/constants.js';
import type { ControlInput, GameSnapshot, RoomState, SubmarineState } from '../../shared/types.js';
import { state as controllerState } from '../controllerState.js';
import { HarpoonCameraView } from '../rendering/HarpoonCameraView.js';
import { clamp, createCard } from '../controllerUiHelpers.js';
import { DEFAULT_GRAPHICS_QUALITY, type GraphicsQuality } from '../rendering/quality.js';
import { DEFAULT_RENDER_TUNING, type RenderTuning } from '../rendering/renderTuning.js';

const GUN_RELOAD_DURATION_MS = (GUN_RELOAD_TICKS / 10) * 1000;

export type WeaponsRoomController = {
  render(turret: 'fore' | 'aft'): void;
  setRenderTuning(tuning: RenderTuning): void;
  setSnapshot(snapshot: GameSnapshot): void;
  setRoomState(state: RoomState | null): void;
  dispose(): void;
};

export interface WeaponsRoomControllerDeps {
  controls: HTMLDivElement | null;
  emitControl: (input: ControlInput) => void;
  getRenderTuning: () => RenderTuning;
  getSubmarinePower: (submarine: SubmarineState) => number;
  getGraphicsQuality: () => GraphicsQuality;
  getFpsOverride?: () => number | undefined;
}

export function createWeaponsRoomController(deps: WeaponsRoomControllerDeps): WeaponsRoomController {
  const {
    controls,
    emitControl,
    getRenderTuning = () => DEFAULT_RENDER_TUNING,
    getGraphicsQuality = () => DEFAULT_GRAPHICS_QUALITY,
    getFpsOverride
  } = deps;

  let cameraView: HarpoonCameraView | null = null;
  let currentTurret: 'fore' | 'aft' = 'fore';
  // Alternates between port and starboard each shot
  let nextSide: 'port' | 'starboard' = 'port';
  let reloading = false;
  let reloadStartMs = 0;
  let reloadFrameId: number | null = null;

  let fireButton: HTMLButtonElement | null = null;
  let reloadBar: HTMLDivElement | null = null;
  let reloadLabel: HTMLDivElement | null = null;

  const aimState = { yaw: 0, pitch: 0 };

  const setReloadProgress = (progress: number): void => {
    if (reloadBar) reloadBar.style.width = `${Math.round(progress * 100)}%`;
  };

  const updateButtonState = (): void => {
    if (fireButton) {
      fireButton.disabled = reloading;
      fireButton.textContent = reloading ? 'RELOADING...' : 'FIRE';
    }
    if (reloadLabel) {
      if (reloading) {
        const remaining = Math.max(0, GUN_RELOAD_DURATION_MS - (performance.now() - reloadStartMs));
        reloadLabel.textContent = `Reloading: ${Math.ceil(remaining / 100) / 10}s`;
      } else {
        reloadLabel.textContent = `Ready — ${nextSide === 'port' ? 'PORT' : 'STBD'} gun next`;
      }
    }
  };

  const advanceReload = (timestampMs: number): void => {
    if (!reloading) return;
    const elapsed = timestampMs - reloadStartMs;
    setReloadProgress(Math.min(1, elapsed / GUN_RELOAD_DURATION_MS));
    if (elapsed >= GUN_RELOAD_DURATION_MS) {
      reloading = false;
      updateButtonState();
      reloadFrameId = null;
      return;
    }
    reloadFrameId = window.requestAnimationFrame(advanceReload);
  };

  const startReload = (): void => {
    if (reloading) return;
    reloading = true;
    reloadStartMs = performance.now();
    setReloadProgress(0);
    updateButtonState();
    reloadFrameId = window.requestAnimationFrame(advanceReload);
  };

  const fire = (): void => {
    if (reloading || !controllerState.lastSnapshot) return;

    const sub = controllerState.lastSnapshot.submarine;
    // Combine submarine world yaw (degrees) with camera yaw offset (degrees) → world aim direction
    const worldYawDeg = sub.yaw + aimState.yaw;
    const worldYawRad = THREE.MathUtils.degToRad(worldYawDeg);
    const aimX = Math.sin(worldYawRad);
    const aimZ = Math.cos(worldYawRad);

    const side = nextSide;
    nextSide = side === 'port' ? 'starboard' : 'port';

    emitControl({
      action: side === 'port' ? 'weapons_fire_port' : 'weapons_fire_starboard',
      turret: currentTurret,
      aimX,
      aimZ
    } as ControlInput);

    startReload();
  };

  return {
    render(turret: 'fore' | 'aft'): void {
      currentTurret = turret;
      if (!controls) return;

      controls.classList.add('camera-role-layout');
      const cameraCard = createCard(
        `${turret === 'fore' ? 'Fore' : 'Aft'} gun turret camera`,
        'Drag to aim. Tap FIRE to shoot — guns alternate port and starboard each shot.'
      );
      cameraCard.classList.add('ctrl-room-main-card');

      const cameraViewport = document.createElement('div');
      cameraViewport.className = 'harpoon-camera-viewport role-camera-viewport';

      // Crosshair reticle
      const reticle = document.createElement('div');
      reticle.style.cssText = 'position:absolute;left:50%;top:50%;width:34px;height:34px;pointer-events:none;transform:translate(-50%,-50%);box-sizing:border-box;border:1px solid rgba(255,120,80,0.92);border-radius:50%;box-shadow:0 0 10px rgba(255,120,80,0.45);z-index:1';
      reticle.innerHTML = '<div style="position:absolute;left:50%;top:0;width:2px;height:100%;background:rgba(255,120,80,0.9);transform:translateX(-50%);"></div><div style="position:absolute;top:50%;left:0;width:100%;height:2px;background:rgba(255,120,80,0.9);transform:translateY(-50%);"></div>';
      cameraViewport.append(reticle);

      cameraCard.append(cameraViewport);
      controls.append(cameraCard);

      cameraView?.dispose();
      cameraView = new HarpoonCameraView(
        cameraViewport,
        getGraphicsQuality(),
        getRenderTuning(),
        getFpsOverride?.()
      );
      if (controllerState.lastRoomState) cameraView.setRoomState(controllerState.lastRoomState);
      if (controllerState.lastSnapshot) cameraView.setSnapshot(controllerState.lastSnapshot);
      cameraView.setYawPitch(aimState.yaw, aimState.pitch);

      // Drag-to-aim — same pattern as HarpoonRoomController
      const updateAim = (yaw: number, pitch: number): void => {
        aimState.yaw = yaw;
        aimState.pitch = clamp(pitch, -89, 89);
        cameraView?.setYawPitch(aimState.yaw, aimState.pitch);
      };

      let dragging = false;
      let dragX = 0;
      let dragY = 0;
      cameraViewport.style.touchAction = 'none';
      cameraViewport.addEventListener('pointerdown', (e) => {
        dragging = true; dragX = e.clientX; dragY = e.clientY;
        cameraViewport.setPointerCapture(e.pointerId);
      });
      cameraViewport.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const rect = cameraViewport.getBoundingClientRect();
        const dyaw = -((e.clientX - dragX) / Math.max(1, rect.width)) * 90;
        const dpitch = -((e.clientY - dragY) / Math.max(1, rect.height)) * 90;
        dragX = e.clientX; dragY = e.clientY;
        updateAim(aimState.yaw + dyaw, aimState.pitch + dpitch);
      });
      const stopDrag = (): void => { dragging = false; };
      cameraViewport.addEventListener('pointerup', stopDrag);
      cameraViewport.addEventListener('pointercancel', stopDrag);

      // Controls below camera
      const controlsRow = document.createElement('div');
      controlsRow.style.cssText = 'display:flex;flex-direction:column;gap:8px;padding:10px 12px';

      fireButton = document.createElement('button');
      fireButton.textContent = 'FIRE';
      fireButton.style.cssText = 'width:100%;padding:14px;font-size:16px;font-weight:700;letter-spacing:2px;background:#3a1010;color:#ff8060;border:1px solid #ff4020;border-radius:4px;cursor:pointer;';
      fireButton.addEventListener('click', fire);
      controlsRow.append(fireButton);

      reloadBar = document.createElement('div');
      const reloadBarInner = document.createElement('div');
      reloadBarInner.style.cssText = 'background:#ff4020;height:100%;width:100%;border-radius:2px;transition:width 0.05s';
      reloadBar = reloadBarInner;
      const reloadBarOuter = document.createElement('div');
      reloadBarOuter.style.cssText = 'background:#1a0808;height:4px;border-radius:2px;overflow:hidden';
      reloadBarOuter.append(reloadBarInner);
      controlsRow.append(reloadBarOuter);

      reloadLabel = document.createElement('div');
      reloadLabel.style.cssText = 'font-size:11px;color:#aaa;text-align:center';
      controlsRow.append(reloadLabel);

      controls.append(controlsRow);
      updateButtonState();
    },

    setRenderTuning(tuning: RenderTuning): void {
      cameraView?.setRenderTuning(tuning);
    },

    setSnapshot(snapshot: GameSnapshot): void {
      cameraView?.setSnapshot(snapshot);
    },

    setRoomState(state: RoomState | null): void {
      if (state) cameraView?.setRoomState(state);
    },

    dispose(): void {
      if (reloadFrameId !== null) {
        window.cancelAnimationFrame(reloadFrameId);
        reloadFrameId = null;
      }
      cameraView?.dispose();
      cameraView = null;
      fireButton = null;
      reloadBar = null;
      reloadLabel = null;
    }
  };
}
